// DESCRIPTION: Small date helpers for the booking widget — formats ISO (YYYY-MM-DD)
// DESCRIPTION: dates for display without timezone drift, using the browser locale.

import { __ } from '@wordpress/i18n';

/**
 * Parse an ISO date string (YYYY-MM-DD) into a local Date at midnight.
 *
 * Building the Date from explicit parts avoids the UTC-parsing drift that
 * `new Date( '2026-05-09' )` introduces in some timezones.
 *
 * @param {string} iso ISO date string.
 *
 * @return {Date|null} Local Date, or null if the input is not a valid ISO date.
 */
export function parseISO( iso ) {
	if ( ! iso ) {
		return null;
	}

	const [ y, m, d ] = iso.split( '-' ).map( Number );

	if ( ! y || ! m || ! d ) {
		return null;
	}

	return new Date( y, m - 1, d );
}

/**
 * Format an ISO date as a long, human label, e.g. "Tuesday, 5 May 2026".
 *
 * @param {string} iso ISO date string.
 *
 * @return {string} Formatted date, or '' if the input is invalid.
 */
export function formatDateLong( iso ) {
	const d = parseISO( iso );

	if ( ! d ) {
		return '';
	}

	return d.toLocaleDateString( undefined, {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	} );
}

/**
 * Format an ISO date as a medium label, e.g. "Tue, 5 May".
 *
 * @param {string} iso ISO date string.
 *
 * @return {string} Formatted date, or '' if the input is invalid.
 */
export function formatDateMedium( iso ) {
	const d = parseISO( iso );

	if ( ! d ) {
		return '';
	}

	return d.toLocaleDateString( undefined, {
		weekday: 'short',
		day: 'numeric',
		month: 'short',
	} );
}

/**
 * Human label for an event type slug.
 *
 * @param {string} type Event type slug ('one-on-one' | 'group' | 'event').
 *
 * @return {string} Translated label.
 */
export function eventTypeLabel( type ) {
	switch ( type ) {
		case 'one-on-one':
			return __( '1:1 Session', 'nemtly-booking' );
		case 'group':
			return __( 'Group Session', 'nemtly-booking' );
		case 'event':
			return __( 'Event', 'nemtly-booking' );
		default:
			return __( 'Session', 'nemtly-booking' );
	}
}

/**
 * Strip HTML tags and collapse whitespace from a string for safe plain-text display.
 *
 * @param {string} html Possibly HTML-bearing string.
 *
 * @return {string} Plain text.
 */
export function stripHtml( html ) {
	if ( ! html ) {
		return '';
	}

	return html
		.replace( /<[^>]*>/g, ' ' )
		.replace( /\s+/g, ' ' )
		.trim();
}

/**
 * Format a money amount with a currency symbol, e.g. "$120.00".
 *
 * @param {string} symbol Currency symbol.
 * @param {number} amount Numeric amount.
 *
 * @return {string} Formatted amount.
 */
export function formatMoney( symbol, amount ) {
	const value = Number.isFinite( amount ) ? amount : 0;

	return `${ symbol || '' }${ value.toFixed( 2 ) }`;
}
